import { Group, Progress, Stack, Text } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ratePassword } from '../password/policy';

/**
 * How the password policy looks. What it *is* lives in shared/password/policy, so a screen can
 * validate against it without rendering anything.
 */

function Requirement({ met, children }: { met: boolean; children: React.ReactNode }) {
  return (
    <Group gap={4} wrap="nowrap">
      <IconCheck
        size={14}
        color={met ? 'var(--mantine-color-green-7)' : 'var(--mantine-color-gray-5)'}
      />
      <Text size="xs" c={met ? 'green.7' : 'dimmed'}>
        {children}
      </Text>
    </Group>
  );
}

/** The strength bar. Renders nothing until something has been typed. */
export function PasswordMeter({ password }: { password: string }) {
  const { t } = useTranslation();
  if (password.length === 0) return null;

  const { score } = ratePassword(password);
  const color = score <= 1 ? 'red' : score === 2 ? 'orange' : score === 3 ? 'yellow' : 'green';
  const label =
    score <= 1
      ? t('registration.complete.strengthWeak')
      : score === 2
        ? t('registration.complete.strengthFair')
        : score === 3
          ? t('registration.complete.strengthGood')
          : t('registration.complete.strengthStrong');

  return (
    <Stack gap={4}>
      <Progress value={(score / 4) * 100} color={color} size="xs" />
      <Text size="xs" c={`${color}.7`}>
        {label}
      </Text>
    </Stack>
  );
}

/** The four requirements, ticked as they are met. Always visible, so nothing is a surprise. */
export function PasswordRules({ password }: { password: string }) {
  const { t } = useTranslation();
  const strength = ratePassword(password);

  return (
    <Stack gap={6} p="sm" style={{ background: 'var(--mantine-color-gray-0)', borderRadius: 8 }}>
      <Text size="xs" tt="uppercase" fw={500} c="dimmed">
        {t('registration.complete.requirements')}
      </Text>
      {/* wrap, because four of these do not fit across a phone */}
      <Group gap="md" wrap="wrap">
        <Requirement met={strength.hasMinLen}>{t('registration.complete.req12Chars')}</Requirement>
        <Requirement met={strength.hasUpperLower}>
          {t('registration.complete.reqUpperLower')}
        </Requirement>
        <Requirement met={strength.hasNumber}>{t('registration.complete.reqNumber')}</Requirement>
        <Requirement met={strength.hasSymbol}>{t('registration.complete.reqSymbol')}</Requirement>
      </Group>
    </Stack>
  );
}
